/**
 * Mounts the game. No StrictMode: `Result` plays its completion chime from a
 * mount effect, and StrictMode's dev double-mount would play it twice.
 */

import { AppwriteAuth } from "@aybuben/client/auth/appwrite.ts";
import { AppwriteRemote } from "@aybuben/client/sync/appwrite.ts";
import { PostHogProvider, PostHogErrorBoundary } from "@posthog/react";
import { ID, Permission, Query, Role } from "appwrite";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import {
	account,
	DB,
	DELETE_FUNCTION,
	EVENTS,
	functions,
	SNAPSHOTS,
	tables,
} from "./game/appwrite/client.ts";
import { webStartOAuth } from "./game/auth/oauth.ts";
import { installTheme } from "./game/device/theme.ts";
import posthog from "./game/posthog.ts";
import Root from "./game/Root.tsx";
import { registerShellWorker } from "./register-sw.ts";

import "./index.css";

installTheme();
registerShellWorker();

const elem = document.getElementById("root")!;
const auth = new AppwriteAuth({
	account,
	functions,
	newId: () => ID.unique(),
	deleteFunctionId: DELETE_FUNCTION,
	startOAuth: webStartOAuth(account),
});
const remote = new AppwriteRemote({
	tables,
	Query,
	Permission,
	Role,
	databaseId: DB,
	eventsTable: EVENTS,
	snapshotsTable: SNAPSHOTS,
});
const app = <Root auth={auth} remote={remote} />;

const root = import.meta.hot ? (import.meta.hot.data.root ??= createRoot(elem)) : createRoot(elem);

root.render(
	<PostHogProvider client={posthog}>
		<PostHogErrorBoundary fallback={<div>Something went wrong</div>}>
			<BrowserRouter>{app}</BrowserRouter>
		</PostHogErrorBoundary>
	</PostHogProvider>,
);
